import clsx from 'clsx';
import type { ReactNode } from 'react';
import { humanize } from '@/lib/format';
import styles from './Badge.module.css';

export type Tone = 'neutral' | 'accent' | 'info' | 'success' | 'warning' | 'danger';

export interface BadgeProps {
  tone?: Tone;
  /** Leading status dot. */
  dot?: boolean;
  children: ReactNode;
  className?: string;
  title?: string;
}

export function Badge({ tone = 'neutral', dot = false, children, className, title }: BadgeProps) {
  return (
    <span className={clsx(styles.badge, styles[tone], className)} title={title}>
      {dot && <span className={styles.dot} aria-hidden />}
      {children}
    </span>
  );
}

/** Tone of every status the API emits (jobs, images, tasks, uploads, annotations, ML). */
const STATUS_TONES: Readonly<Record<string, Tone>> = {
  // jobs
  queued: 'neutral',
  running: 'info',
  succeeded: 'success',
  failed: 'danger',
  cancelled: 'warning',
  // images
  uploading: 'info',
  processing: 'info',
  ready: 'success',
  // tasks
  new: 'neutral',
  in_progress: 'info',
  submitted: 'accent',
  accepted: 'success',
  rejected: 'danger',
  // uploads / annotations
  pending: 'neutral',
  complete: 'success',
  aborted: 'warning',
  draft: 'neutral',
  // ML service
  disabled: 'neutral',
  cold: 'neutral',
  loading: 'info',
  warming: 'info',
  encoding: 'info',
  error: 'danger',
};

export interface StatusPillProps {
  status: string;
  /** Override the displayed text (default: humanised status). */
  label?: ReactNode;
  className?: string;
}

/** Status badge with a coloured dot; unknown statuses render neutral. */
export function StatusPill({ status, label, className }: StatusPillProps) {
  const tone = STATUS_TONES[status] ?? 'neutral';
  return (
    <Badge tone={tone} dot className={clsx(status === 'running' && styles.pulse, className)}>
      {label ?? humanize(status)}
    </Badge>
  );
}
